import { useParams } from '@tanstack/react-router'
import {
  CircularProgress,
  FF,
  FieldSet,
  IconSize,
  Page,
  PageFace,
  PageSection,
  PageSectionFace,
  Panel,
  TextMask
} from 'ui/components'
import { Button } from 'ui/components/Buttons'
import { CurrencyCentInput, TextareaInput } from 'ui/components/InputV2'
import { Footer } from 'ui/temp/Footer'
import { Col, Col2x, FormRow } from 'ui/themes/globalStyles'

import { useMRWebPageDepositsManage } from './useMRWebPageDepositsManage'

import * as SC from './styles'

const MRWebPageDepositsManage = () => {
  const { id: MRId, bankAccountId } = useParams({ from: '/_app/mr/$id/$bankAccountId/deposits-edit' })

  const { data, onCancel, onSubmit, isSubmitting, formApi } = useMRWebPageDepositsManage(+MRId, +bankAccountId)

  if (!data) {
    return <CircularProgress centered />
  }

  const { title, titleOverline, bankAccountNumber, enableDepositsOtherActivity } = data

  return (
    <Page
      face={PageFace.SECONDARY}
      title={titleOverline}
      subTitle={title}
      footer={
        <Footer>
          <Button face="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button loading={isSubmitting} onClick={onSubmit} face="primary">
            Save
          </Button>
        </Footer>
      }
    >
      <PageSection
        face={PageSectionFace.THIRD}
        title={
          <span style={{ display: 'flex', columnGap: '10px' }}>
            <span>Bank Account</span>
            <TextMask isBankAccount text={bankAccountNumber} iconSize={IconSize.XS} />
          </span>
        }
      >
        <Panel rounded shadowed collapsible={false} title="Edit Deposits">
          <SC.Box>
            <FieldSet legend="Deposits From Sales">
              <FormRow>
                <Col>
                  <formApi.Field
                    name="cashDeposits"
                    children={(f) => (
                      <FF
                        field={f}
                        label="Cash - Deposits"
                        input={(p) => <CurrencyCentInput {...p} data-qa="cashDeposits" />}
                      />
                    )}
                  />
                </Col>
                <Col>
                  <formApi.Field
                    name="invoices"
                    children={(f) => (
                      <FF
                        field={f}
                        label="Cash - Invoices"
                        input={(p) => <CurrencyCentInput {...p} data-qa="invoices" />}
                      />
                    )}
                  />
                </Col>
              </FormRow>
              <FormRow>
                <Col>
                  <formApi.Field
                    name="atm"
                    children={(f) => (
                      <FF field={f} label="Cash - ATM Load" input={(p) => <CurrencyCentInput {...p} data-qa="atm" />} />
                    )}
                  />
                </Col>
                <Col>
                  <formApi.Field
                    name="internalTransfers"
                    children={(f) => (
                      <FF
                        field={f}
                        label="Internal Transfers"
                        input={(p) => <CurrencyCentInput {...p} data-qa="internalTransfers" />}
                      />
                    )}
                  />
                </Col>
              </FormRow>
              <FormRow>
                <Col>
                  <formApi.Field
                    name="creditDebit"
                    children={(f) => (
                      <FF
                        field={f}
                        label="Credit / Debit Cards"
                        input={(p) => <CurrencyCentInput {...p} data-qa="creditDebit" />}
                      />
                    )}
                  />
                </Col>
                <Col>
                  <formApi.Field
                    name="checks"
                    children={(f) => (
                      <FF field={f} label="Checks" input={(p) => <CurrencyCentInput {...p} data-qa="checks" />} />
                    )}
                  />
                </Col>
              </FormRow>
              <FormRow>
                <Col>
                  <formApi.Field
                    name="other"
                    children={(f) => (
                      <FF field={f} label="Other" input={(p) => <CurrencyCentInput {...p} data-qa="other" />} />
                    )}
                  />
                </Col>
                <Col></Col>
              </FormRow>
            </FieldSet>

            {enableDepositsOtherActivity && (
              <FieldSet legend="Other Banking Activity">
                <FormRow>
                  <Col>
                    <formApi.Field
                      name="oaCashWithdrawals"
                      children={(f) => (
                        <FF
                          field={f}
                          label="Cash Withdrawals"
                          input={(p) => <CurrencyCentInput {...p} data-qa="oaCashWithdrawals" />}
                        />
                      )}
                    />
                  </Col>
                  <Col></Col>
                </FormRow>
                <FormRow>
                  <Col>
                    <formApi.Field
                      name="oaChecksDebits"
                      children={(f) => (
                        <FF
                          field={f}
                          label="Check Debits"
                          input={(p) => <CurrencyCentInput {...p} data-qa="oaChecksDebits" />}
                        />
                      )}
                    />
                  </Col>
                  <Col>
                    <formApi.Field
                      name="oaACHDebits"
                      children={(f) => (
                        <FF
                          field={f}
                          label="ACH Debits"
                          input={(p) => <CurrencyCentInput {...p} data-qa="oaAchDebits" />}
                        />
                      )}
                    />
                  </Col>
                </FormRow>
                <FormRow>
                  <Col>
                    <formApi.Field
                      name="oaDebitCardsCredits"
                      children={(f) => (
                        <FF
                          field={f}
                          label="Debit Cards - Credit"
                          input={(p) => <CurrencyCentInput {...p} data-qa="oaDebitCardsCredits" />}
                        />
                      )}
                    />
                  </Col>
                  <Col>
                    <formApi.Field
                      name="oaDebitCardsDebits"
                      children={(f) => (
                        <FF
                          field={f}
                          label="Debit Cards - Debits"
                          input={(p) => <CurrencyCentInput {...p} data-qa="oaDebitCardsDebits" />}
                        />
                      )}
                    />
                  </Col>
                </FormRow>
                <FormRow>
                  <Col>
                    <formApi.Field
                      name="oaTransferCredits"
                      children={(f) => (
                        <FF
                          field={f}
                          label="Transfer - Credits"
                          input={(p) => <CurrencyCentInput {...p} data-qa="oaTransferCredits" />}
                        />
                      )}
                    />
                  </Col>
                  <Col>
                    <formApi.Field
                      name="oaTransferDebits"
                      children={(f) => (
                        <FF
                          field={f}
                          label="Transfer - Debits"
                          input={(p) => <CurrencyCentInput {...p} data-qa="oaTransferDebits" />}
                        />
                      )}
                    />
                  </Col>
                </FormRow>
                <FormRow>
                  <Col>
                    <formApi.Field
                      name="oaWiresIncoming"
                      children={(f) => (
                        <FF
                          field={f}
                          label="Wires - Incoming"
                          input={(p) => <CurrencyCentInput {...p} data-qa="oaWiresIncoming" />}
                        />
                      )}
                    />
                  </Col>
                  <Col>
                    <formApi.Field
                      name="oaWiresOutgoing"
                      children={(f) => (
                        <FF
                          field={f}
                          label="Wires - Outgoing"
                          input={(p) => <CurrencyCentInput {...p} data-qa="oaWiresOutgoing" />}
                        />
                      )}
                    />
                  </Col>
                </FormRow>
                <FormRow>
                  <Col>
                    <formApi.Field
                      name="oaSweepsIncoming"
                      children={(f) => (
                        <FF
                          field={f}
                          label="Sweeps - Incoming"
                          input={(p) => <CurrencyCentInput {...p} data-qa="oaSweepsIncoming" />}
                        />
                      )}
                    />
                  </Col>
                  <Col>
                    <formApi.Field
                      name="oaSweepsOutgoing"
                      children={(f) => (
                        <FF
                          field={f}
                          label="Sweeps - Outgoing"
                          input={(p) => <CurrencyCentInput {...p} data-qa="oaSweepsOutgoing" />}
                        />
                      )}
                    />
                  </Col>
                </FormRow>
                <FormRow>
                  <Col>
                    <formApi.Field
                      name="oaBankFees"
                      children={(f) => (
                        <FF
                          field={f}
                          label="Bank Fees"
                          input={(p) => <CurrencyCentInput {...p} data-qa="oaBankFees" />}
                        />
                      )}
                    />
                  </Col>
                  <Col></Col>
                </FormRow>
                <FormRow>
                  <Col>
                    <formApi.Field
                      name="oaMoneyOrdersPurchased"
                      children={(f) => (
                        <FF
                          field={f}
                          label="Money Orders Purchased"
                          input={(p) => <CurrencyCentInput {...p} data-qa="oaMoneyOrdersPurchased" />}
                        />
                      )}
                    />
                  </Col>
                  <Col>
                    <formApi.Field
                      name="oaCashiersChecksPurchased"
                      children={(f) => (
                        <FF
                          field={f}
                          label="Cashiers Checks Purchased"
                          input={(p) => <CurrencyCentInput {...p} data-qa="oaCashiersChecksPurchased" />}
                        />
                      )}
                    />
                  </Col>
                </FormRow>
                <FormRow>
                  <Col>
                    <formApi.Field
                      name="oaCreditCardCharges"
                      children={(f) => (
                        <FF
                          field={f}
                          label="CB&T Credit Card - Charges"
                          input={(p) => <CurrencyCentInput {...p} data-qa="oaCreditCardCharges" />}
                        />
                      )}
                    />
                  </Col>
                  <Col>
                    <formApi.Field
                      name="oaCreditCardPayments"
                      children={(f) => (
                        <FF
                          field={f}
                          label="CB&T Credit Card - Payments"
                          input={(p) => <CurrencyCentInput {...p} data-qa="oaCreditCardPayments" />}
                        />
                      )}
                    />
                  </Col>
                </FormRow>
              </FieldSet>
            )}

            <FieldSet>
              <FormRow>
                <Col2x>
                  <formApi.Field
                    name="notes"
                    children={(f) => (
                      <FF field={f} label="Notes" input={(p) => <TextareaInput {...p} data-qa="notes" />} />
                    )}
                  />
                </Col2x>
              </FormRow>
            </FieldSet>
          </SC.Box>
        </Panel>
      </PageSection>
    </Page>
  )
}

export default MRWebPageDepositsManage
